// ADO-1779: drag-to-dismiss for a bottom sheet (the Modal's `bottom` placement).
//
// The handlers go on the sheet's header, the row that holds the grab handle, so a drag in
// the scrolling body still scrolls it. While the pointer is down the sheet follows it
// downward (never up, off the bottom edge) through an inline `transform`, with
// `data-dragging` set so the CSS drops the spring-back transition. On release it dismisses
// when the drag passed SHEET_DISMISS_RATIO of the sheet's height (at least
// SHEET_DISMISS_MIN_PX) or was a flick faster than SHEET_FLICK_VELOCITY over at least
// SHEET_FLICK_MIN_PX; otherwise the inline transform is cleared and the sheet springs back.
// A drag the header never saw end (the sheet closed under it) is dropped when the sheet
// closes, and a move with no button pressed ends the drag in progress, so a hover never drags.
// A press that starts on a button (the Close control) is left to that button, and only the
// primary mouse button drags. A press must also land inside the header in the DOM: React
// bubbles pointer events along the component tree, so a dialog portalled from inside the
// header (the cart panel's Clear confirm) would otherwise start a drag of the sheet behind
// it. Moves and releases need no such check, since they act only on a drag a press in the
// header started.
// Pointer-only, like the scrim press: Escape and Close stay the keyboard path.
import { useCallback, useEffect, useRef, type PointerEvent, type RefObject } from 'react';

/** Share of the sheet's height a drag must pass for the release to dismiss it. */
export const SHEET_DISMISS_RATIO = 0.25;
/** The shortest dismissing drag, for a short sheet. */
export const SHEET_DISMISS_MIN_PX = 64;
/** A release faster than this (px per ms, averaged over the drag) is a dismissing flick. */
export const SHEET_FLICK_VELOCITY = 0.5;
/** A flick must still travel this far, so a quick tap with some jitter never dismisses. */
export const SHEET_FLICK_MIN_PX = 16;

interface DragState {
  readonly pointerId: number;
  readonly startY: number;
  readonly startTime: number;
  offset: number;
}

export interface SheetDragHandlers {
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
}

/** Drag handlers for `sheetRef`'s header, or `undefined` while `enabled` (open) is false. */
export function useSheetDrag(
  enabled: boolean,
  sheetRef: RefObject<HTMLElement | null>,
  onDismiss: () => void,
): SheetDragHandlers | undefined {
  const drag = useRef<DragState | null>(null);

  const release = useCallback(() => {
    drag.current = null;
    const sheet = sheetRef.current;
    if (sheet === null) return;
    sheet.removeAttribute('data-dragging');
    sheet.style.transform = '';
  }, [sheetRef]);

  useEffect(() => {
    if (!enabled) release();
  }, [enabled, release]);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if (!event.currentTarget.contains(event.target as Node)) return;
    if ((event.target as Element).closest('button') !== null) return;
    drag.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTime: performance.now(),
      offset: 0,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const state = drag.current;
      const sheet = sheetRef.current;
      if (state === null || sheet === null || event.pointerId !== state.pointerId) return;
      if (event.buttons === 0) {
        release();
        return;
      }
      state.offset = Math.max(0, event.clientY - state.startY);
      sheet.setAttribute('data-dragging', '');
      sheet.style.transform = `translateY(${state.offset}px)`;
    },
    [release, sheetRef],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const state = drag.current;
      if (state === null || event.pointerId !== state.pointerId) return;
      const height = sheetRef.current?.getBoundingClientRect().height ?? 0;
      const distance = Math.max(SHEET_DISMISS_MIN_PX, height * SHEET_DISMISS_RATIO);
      const elapsed = Math.max(1, performance.now() - state.startTime);
      const dismiss =
        state.offset >= distance ||
        (state.offset >= SHEET_FLICK_MIN_PX && state.offset / elapsed >= SHEET_FLICK_VELOCITY);
      release();
      if (dismiss) onDismiss();
    },
    [onDismiss, release, sheetRef],
  );

  const onPointerCancel = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (drag.current?.pointerId === event.pointerId) release();
    },
    [release],
  );

  return enabled ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } : undefined;
}
